/** Mobile CE.SDK (1.68) requires an explicit scene mode; 1.72+ web saves mode-less scenes by default. */
export const EDITABLE_VIDEO_SCENE_MODE = 'Video';

/** Public CE.SDK asset host. Admin local `/assets` is mkcert and iOS will not fetch it. */
export const CESDK_CDN_ASSET_BASE = 'https://cdn.img.ly/assets/v4/';

/** Bundled 1.79 asset folders that only exist on the v5 CDN (v4 returns 404). */
export const CESDK_V5_CDN_ASSET_BASE = 'https://cdn.img.ly/assets/v5/';
export const CESDK_V5_ONLY_ASSET_FOLDERS = [
  'ly.img.caption.presets',
  'ly.img.color.palette',
  'ly.img.filter',
  'ly.img.text.components',
  'ly.img.vector.shape',
];

const LOCAL_ADMIN_ASSET_HOST = 'local-admin.bnservers.com';
const UBQ_PREFIX = /^(UBQ\d+)/;

function utf8ToBase64(value) {
  if (typeof Buffer !== 'undefined') {
    return Buffer.from(value, 'utf8').toString('base64');
  }

  return btoa(unescape(encodeURIComponent(value)));
}

function base64ToUtf8(value) {
  const padded = value + '='.repeat((4 - (value.length % 4)) % 4);
  if (typeof Buffer !== 'undefined') {
    return Buffer.from(padded, 'base64').toString('utf8');
  }

  return decodeURIComponent(escape(atob(padded)));
}

function splitUbqScene(sceneString) {
  const match = sceneString.match(UBQ_PREFIX);
  if (!match) {
    return { prefix: '', payload: sceneString };
  }

  try {
    const payload = base64ToUtf8(
      sceneString.slice(match[1].length).replace(/\s/g, '')
    );
    if (!payload.includes('ly.img') && !payload.includes('"format"')) {
      return { prefix: '', payload: sceneString };
    }
    return { prefix: match[1], payload };
  } catch {
    return { prefix: '', payload: sceneString };
  }
}

/**
 * A URI prefix can appear plain, JSON-escaped (`\/`) or URL-encoded in a scene,
 * so each rule expands to all three spellings.
 */
function allSpellings(from, to) {
  const jsonEscape = (value) => value.replace(/\//g, '\\/');
  return [
    [from, to],
    [jsonEscape(from), jsonEscape(to)],
    [encodeURIComponent(from), encodeURIComponent(to)],
  ];
}

/** Points CE.SDK asset URIs in a decoded scene payload at a CDN mobile can fetch. */
function applyAdminAssetHostReplacements(payload, origin) {
  const adminOriginRules =
    origin && !origin.includes('cdn.img.ly')
      ? [[`${origin.replace(/\/$/, '')}/assets/`, CESDK_CDN_ASSET_BASE]]
      : [];

  // Rules run in order: step 2 depends on the v4 URIs step 1 produces.
  const rules = [
    // 1. Admin's bundled /assets (current origin or local host) -> public v4 CDN.
    ...adminOriginRules,
    [`https://${LOCAL_ADMIN_ASSET_HOST}/assets/`, CESDK_CDN_ASSET_BASE],
    [`http://${LOCAL_ADMIN_ASSET_HOST}/assets/`, CESDK_CDN_ASSET_BASE],
    // 2. v4 -> v5 for folders that 404 on v4 (e.g. shape placeholder images).
    ...CESDK_V5_ONLY_ASSET_FOLDERS.map((folder) => [
      `${CESDK_CDN_ASSET_BASE}${folder}/`,
      `${CESDK_V5_CDN_ASSET_BASE}${folder}/`,
    ]),
  ];

  return (
    rules
      // Expand each [from, to] rule into its plain, JSON-escaped and
      // URL-encoded pairs, keeping one flat list in the original rule order.
      .flatMap(([from, to]) => allSpellings(from, to))
      // Apply each pair to the scene in turn (split/join = replace all), so
      // every pair sees the output of the pairs before it.
      .reduce((scene, [from, to]) => scene.split(from).join(to), payload)
  );
}

export function rewriteLocalAdminCesdkAssetUris(
  sceneString,
  origin = typeof window !== 'undefined' ? window.location?.origin : undefined
) {
  if (typeof sceneString !== 'string' || !sceneString) {
    return sceneString;
  }

  const { prefix, payload } = splitUbqScene(sceneString);
  const rewritten = applyAdminAssetHostReplacements(payload, origin);
  if (rewritten === payload) {
    return sceneString;
  }

  return prefix ? `${prefix}${utf8ToBase64(rewritten)}` : rewritten;
}

export function ensureEditableVideoSceneModeForMobile(cesdk) {
  const { engine } = cesdk ?? {};
  const setMode = engine?.scene?.setMode;

  if (typeof setMode !== 'function') {
    return;
  }

  const currentMode = engine.scene.getMode?.() ?? null;

  if (currentMode !== EDITABLE_VIDEO_SCENE_MODE) {
    setMode.call(engine.scene, EDITABLE_VIDEO_SCENE_MODE);
  }
}

export function readEditableVideoSceneMetadata(cesdk) {
  const pages = cesdk?.engine?.scene?.getPages?.() ?? [];

  if (!pages.length) {
    return null;
  }

  const page = pages[0];
  const { engine } = cesdk;
  const width = Math.round(engine.block.getWidth(page));
  const height = Math.round(engine.block.getHeight(page));
  const durationSeconds = engine.block.getDuration(page);

  if (
    !Number.isFinite(width) ||
    !Number.isFinite(height) ||
    !Number.isFinite(durationSeconds) ||
    width <= 0 ||
    height <= 0 ||
    durationSeconds < 0
  ) {
    return null;
  }

  return {
    width,
    height,
    duration_seconds: durationSeconds,
  };
}

export function formatVideoDuration(durationSeconds) {
  if (!Number.isFinite(durationSeconds) || durationSeconds < 0) {
    return null;
  }

  const totalSeconds = Math.round(durationSeconds);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

export function formatEditableVideoMetadataLabel(metadata) {
  if (!metadata?.width || !metadata?.height) {
    return null;
  }

  const resolution = `${metadata.width} x ${metadata.height}`;
  const duration = formatVideoDuration(metadata.duration_seconds);

  return duration ? `${resolution} • ${duration}` : resolution;
}

export async function loadEditableVideoScene(cesdk, scene) {
  await cesdk.engine.scene.loadFromString(scene);
  await cesdk.actions.run('zoom.toPage', { page: 'first' });
}
